import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getInviteByToken, updateClient } from '../../services/clientsService';
import { setUserProfile } from '../../services/userProfileService';
import { addBrokerLink } from '../../services/brokerLinksService';
import { linkFundInvestorToUser } from '../../services/fundInvestorsService';
import {
  canAcceptInvitationWithCurrentUser,
  formatInvitationAcceptError,
} from '../../utils/invitationEmail';
import { linkPocketDealRecipient, pocketLinkErrorReason } from '../../services/pocketDealLinkService';
import {
  getShare,
  getPocketClientIdFromLocationHash,
  getShareTokenFromLocationHash,
} from '../../services/shareService';
import AccountTypeSignupFields from './AccountTypeSignupFields';
import AuthLegalLinks from '../legal/AuthLegalLinks';
import {
  buildLegalAcceptanceFields,
  registerPersonaProfile,
  validatePersonaVerification,
} from '../../utils/personaRegistration';
import { isSignupPersonaSelectable } from '../../constants/personas';

const SignupPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [emailAlreadyInUse, setEmailAlreadyInUse] = useState(false);
  const [inviteData, setInviteData] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(true);
  const [acceptedLegal, setAcceptedLegal] = useState(false);
  const { currentUser, signup, loginWithGoogle, refreshUserProfile, logout } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const signupSearchParams = new URLSearchParams(location.search);
  const invitationToken = signupSearchParams.get('invitation');
  /** HashRouter : `location.search` est en principe rempli ; repli sur le hash si besoin (évite callable avec token vide / mauvais id → erreur type « 404 »). */
  const pocketShareToken =
    signupSearchParams.get('share') || getShareTokenFromLocationHash();
  const pocketClientId =
    signupSearchParams.get('pc') || getPocketClientIdFromLocationHash();

  useEffect(() => {
    if (!invitationToken) {
      setInviteLoading(false);
      return;
    }
    getInviteByToken(invitationToken)
      .then((data) => {
        if (data) {
          setInviteData(data);
          if (data.email) setEmail(data.email);
        }
        setInviteLoading(false);
      })
      .catch(() => setInviteLoading(false));
  }, [invitationToken]);

  const [linkingBroker, setLinkingBroker] = useState(false);
  const [linkError, setLinkError] = useState(null);
  const redirectTimerRef = useRef(null);
  const [selectedPersona, setSelectedPersona] = useState('investisseur');
  const [verification, setVerification] = useState({
    companyName: '',
    phone: '',
    street: '',
    city: '',
    province: '',
    postalCode: '',
    country: 'Canada',
  });

  const isForcedInvestorSignup = Boolean(
    inviteData?.clientId ||
      inviteData?.fundInvestorId ||
      (pocketShareToken && pocketClientId),
  );

  const loggedInInvitationEmailMismatch = useMemo(() => {
    if (!currentUser?.email || !inviteData?.email) return false;
    if (!inviteData?.clientId && !inviteData?.fundInvestorId) return false;
    return !canAcceptInvitationWithCurrentUser(inviteData.email, currentUser.email);
  }, [currentUser?.email, inviteData]);

  // Investisseur déjà connecté qui ouvre le lien d'invitation : lier le courtier, afficher 2 s, puis rediriger
  useEffect(() => {
    if (!currentUser?.uid || !inviteData?.clientId || inviteLoading) return;
    if (!canAcceptInvitationWithCurrentUser(inviteData.email, currentUser?.email)) return;
    let cancelled = false;
    setLinkError(null);
    setLinkingBroker(true);
    (async () => {
      try {
        try {
          await updateClient(inviteData.clientId, { clientUserId: currentUser.uid });
        } catch (e) {
          // Client déjà lié (ex. deuxième ouverture du lien) : on continue pour mettre à jour le profil et brokerLinks
          console.warn('SignupPage: updateClient', e?.message);
        }
        await setUserProfile(currentUser.uid, {
          persona: 'investisseur',
          onboardingCompleted: true,
          brokerClientId: inviteData.clientId,
          brokerUid: inviteData.brokerUid,
        });
        await addBrokerLink(currentUser.uid, inviteData.brokerUid, inviteData.clientId, inviteData.brokerDisplayName || '');
        if (refreshUserProfile) await refreshUserProfile();
        if (cancelled) return;
        // Laisser le message visible 2 secondes avant la redirection
        redirectTimerRef.current = setTimeout(() => {
          if (!cancelled) {
            navigate('/', { replace: true, state: { shareAdded: false, invitationAccepted: true } });
          }
        }, 2000);
      } catch (e) {
        console.warn('SignupPage: accept invitation while logged in', e?.message);
        if (!cancelled) {
          setLinkError(formatInvitationAcceptError(e, t, inviteData?.email));
          setLinkingBroker(false);
        }
      }
    })();
    return () => {
      cancelled = true;
      if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
    };
  }, [currentUser?.uid, currentUser?.email, inviteData, inviteLoading, navigate, refreshUserProfile, t]);

  // Utilisateur déjà connecté qui ouvre un lien d'invitation d'investisseur de capitaux (fundInvestorId)
  useEffect(() => {
    if (!currentUser?.uid || !inviteData?.fundInvestorId || inviteLoading) return;
    if (!canAcceptInvitationWithCurrentUser(inviteData.email, currentUser?.email)) return;
    let cancelled = false;
    setLinkError(null);
    const timeoutId = setTimeout(() => {
      if (!cancelled) setLinkError(t('auth.linkingBrokerError'));
    }, 12000);
    (async () => {
      try {
        await linkFundInvestorToUser(inviteData.fundInvestorId, currentUser.uid);
        if (cancelled) return;
        await setUserProfile(currentUser.uid, {
          persona: 'investisseur',
          onboardingCompleted: true,
        });
        if (cancelled) return;
        clearTimeout(timeoutId);
        navigate('/', {
          replace: true,
          state: {
            invitationAccepted: true,
            focusTab: 'investorFunds',
            openFundInvestorId: inviteData.fundInvestorId,
          },
        });
        if (refreshUserProfile) refreshUserProfile().catch(() => {});
      } catch (e) {
        console.warn('SignupPage: link fund investor invite', e?.message);
        clearTimeout(timeoutId);
        if (!cancelled) setLinkError(formatInvitationAcceptError(e, t, inviteData?.email));
      }
    })();
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [currentUser?.uid, currentUser?.email, inviteData, inviteLoading, navigate, refreshUserProfile, t]);

  /** HashRouter : garder share+pc dans l’URL après auth si on les a (y compris lus depuis le hash). */
  const homePathAfterSignup = () => {
    if (pocketShareToken && pocketClientId) {
      const q = new URLSearchParams();
      q.set('share', pocketShareToken);
      q.set('pc', pocketClientId);
      return `/?${q.toString()}`;
    }
    if (location.search) return `/${location.search}`;
    return '/';
  };

  const getAuthErrorMessage = (err) => {
    const code = err?.code || '';
    if (code === 'auth/email-already-in-use') return invitationToken ? t('auth.error.emailAlreadyInUseUseLogin') : t('auth.error.emailAlreadyInUse');
    if (code === 'auth/weak-password') return t('auth.error.weakPassword');
    if (code === 'auth/invalid-email') return t('auth.error.invalidEmail');
    if (code === 'auth/operation-not-allowed') return t('auth.error.operationNotAllowed');
    if (code === 'auth/too-many-requests') return t('auth.error.tooManyRequests');
    if (code === 'auth/account-exists-with-different-credential') return t('auth.error.accountExistsWithDifferentCredential');
    return t('auth.signup.error') + (err?.message ? ` (${err.message})` : '');
  };

  const validateAccountType = () => {
    if (isForcedInvestorSignup) return true;
    if (!selectedPersona) {
      setError(t('signup.accountType.required'));
      return false;
    }
    if (!isSignupPersonaSelectable(selectedPersona)) {
      setError(t('signup.accountType.notAvailable'));
      return false;
    }
    const validationKey = validatePersonaVerification(selectedPersona, verification);
    if (validationKey) {
      setError(t(validationKey));
      return false;
    }
    return true;
  };

  const validateLegalAcceptance = () => {
    if (acceptedLegal) return true;
    setError(t('signup.legal.required'));
    return false;
  };

  const legalProfileFields = () => buildLegalAcceptanceFields();

  const applyStandardSignupProfile = async (uid, signedUpEmail, displayName = '') => {
    if (isForcedInvestorSignup) return;
    await registerPersonaProfile({
      uid,
      email: signedUpEmail,
      persona: selectedPersona || 'investisseur',
      displayName,
      locale: language === 'en' ? 'en' : 'fr',
      verification,
      legalAccepted: true,
    });
    if (refreshUserProfile) await refreshUserProfile();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setEmailAlreadyInUse(false);
    if (!validateLegalAcceptance()) return;
    if (!validateAccountType()) return;
    try {
      const cred = await signup(email, password);
      const uid = cred?.user?.uid;
      const signedUpEmail = cred?.user?.email || email;
      if (inviteData?.email && !canAcceptInvitationWithCurrentUser(inviteData.email, signedUpEmail)) {
        setError(String(t('auth.invitationSignupEmailMismatch')).replace(/\{email\}/g, String(inviteData.email).trim()));
        return;
      }
      if (uid && inviteData?.clientId) {
        await updateClient(inviteData.clientId, { clientUserId: uid });
        await setUserProfile(uid, {
          persona: 'investisseur',
          onboardingCompleted: true,
          brokerClientId: inviteData.clientId,
          brokerUid: inviteData.brokerUid,
          ...legalProfileFields(),
        });
        await addBrokerLink(uid, inviteData.brokerUid, inviteData.clientId, inviteData.brokerDisplayName || '');
      } else if (uid && inviteData?.fundInvestorId) {
        await linkFundInvestorToUser(inviteData.fundInvestorId, uid);
        await setUserProfile(uid, {
          persona: 'investisseur',
          onboardingCompleted: true,
          ...legalProfileFields(),
        });
        navigate('/', {
          replace: true,
          state: {
            invitationAccepted: true,
            focusTab: 'investorFunds',
            openFundInvestorId: inviteData.fundInvestorId,
          },
        });
        return;
      } else if (uid && pocketShareToken && pocketClientId && !inviteData?.clientId) {
        const sh = await getShare(pocketShareToken);
        if (sh?.uid) {
          try {
            await updateClient(pocketClientId, { clientUserId: uid });
          } catch (e) {
            console.warn('SignupPage: updateClient pocket', e?.message);
          }
          await setUserProfile(uid, {
            persona: 'investisseur',
            onboardingCompleted: true,
            brokerClientId: pocketClientId,
            brokerUid: sh.uid,
            ...legalProfileFields(),
          });
          await addBrokerLink(uid, sh.uid, pocketClientId, '');
        } else {
          await setUserProfile(uid, {
            persona: 'investisseur',
            onboardingCompleted: true,
            ...legalProfileFields(),
          });
        }
        if (refreshUserProfile) await refreshUserProfile();
        try {
          await linkPocketDealRecipient({ shareToken: pocketShareToken, clientId: pocketClientId });
        } catch (e) {
          const reason = pocketLinkErrorReason(e);
          console.warn('SignupPage: linkPocketDealRecipient', e?.message, reason);
          if (reason === 'email_mismatch') {
            navigate(homePathAfterSignup(), { replace: true, state: { pocketDealLinkEmailMismatch: true } });
            return;
          }
        }
      } else if (uid) {
        await applyStandardSignupProfile(uid, signedUpEmail, cred?.user?.displayName || '');
      }
      navigate(homePathAfterSignup(), { replace: true });
    } catch (err) {
      setEmailAlreadyInUse(err?.code === 'auth/email-already-in-use');
      setError(getAuthErrorMessage(err));
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    if (!validateLegalAcceptance()) return;
    if (!validateAccountType()) return;
    try {
      const result = await loginWithGoogle();
      const uid = result?.user?.uid;
      const googleEmail = result?.user?.email;
      if (inviteData?.email && !canAcceptInvitationWithCurrentUser(inviteData.email, googleEmail)) {
        setError(String(t('auth.invitationSignupEmailMismatch')).replace(/\{email\}/g, String(inviteData.email).trim()));
        return;
      }
      if (uid && inviteData?.clientId) {
        await updateClient(inviteData.clientId, { clientUserId: uid });
        await setUserProfile(uid, {
          persona: 'investisseur',
          onboardingCompleted: true,
          brokerClientId: inviteData.clientId,
          brokerUid: inviteData.brokerUid,
          ...legalProfileFields(),
        });
        await addBrokerLink(uid, inviteData.brokerUid, inviteData.clientId, inviteData.brokerDisplayName || '');
      } else if (uid && inviteData?.fundInvestorId) {
        await linkFundInvestorToUser(inviteData.fundInvestorId, uid);
        await setUserProfile(uid, {
          persona: 'investisseur',
          onboardingCompleted: true,
          ...legalProfileFields(),
        });
        navigate('/', {
          replace: true,
          state: {
            invitationAccepted: true,
            focusTab: 'investorFunds',
            openFundInvestorId: inviteData.fundInvestorId,
          },
        });
        return;
      } else if (uid && pocketShareToken && pocketClientId && !inviteData?.clientId) {
        const sh = await getShare(pocketShareToken);
        if (sh?.uid) {
          try {
            await updateClient(pocketClientId, { clientUserId: uid });
          } catch (e) {
            console.warn('SignupPage: updateClient pocket', e?.message);
          }
          await setUserProfile(uid, {
            persona: 'investisseur',
            onboardingCompleted: true,
            brokerClientId: pocketClientId,
            brokerUid: sh.uid,
            ...legalProfileFields(),
          });
          await addBrokerLink(uid, sh.uid, pocketClientId, '');
        } else {
          await setUserProfile(uid, {
            persona: 'investisseur',
            onboardingCompleted: true,
            ...legalProfileFields(),
          });
        }
        if (refreshUserProfile) await refreshUserProfile();
        try {
          await linkPocketDealRecipient({ shareToken: pocketShareToken, clientId: pocketClientId });
        } catch (e) {
          const reason = pocketLinkErrorReason(e);
          console.warn('SignupPage: linkPocketDealRecipient', e?.message, reason);
          if (reason === 'email_mismatch') {
            navigate(homePathAfterSignup(), { replace: true, state: { pocketDealLinkEmailMismatch: true } });
            return;
          }
        }
      } else if (uid) {
        await applyStandardSignupProfile(uid, googleEmail || '', result?.user?.displayName || '');
      }
      navigate(homePathAfterSignup(), { replace: true });
    } catch (err) {
      setError(getAuthErrorMessage(err) || t('auth.google.error'));
    }
  };

  // Mauvais compte connecté : même navigateur, courriel différent de l'invitation
  if (
    currentUser &&
    invitationToken &&
    inviteData &&
    !inviteLoading &&
    (inviteData.clientId || inviteData.fundInvestorId) &&
    loggedInInvitationEmailMismatch
  ) {
    const invited = String(inviteData.email || '').trim() || '—';
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100 px-4 py-6">
        <div className="w-full max-w-md space-y-4 sm:space-y-6">
          <div className="flex justify-center">
            <img
              src="/rentalyzer-logo.png"
              alt="Rentalyzer logo"
              className="h-24 sm:h-48 w-auto"
            />
          </div>
          <div className="p-5 sm:p-8 space-y-4 bg-white rounded shadow text-center">
          <p className="text-red-700 text-sm leading-relaxed">
            {String(t('auth.invitationWrongAccount')).replace(/\{email\}/g, invited)}
          </p>
          <p className="text-xs text-gray-600">{t('auth.invitationWrongAccountHelp')}</p>
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-center">
            <button
              type="button"
              onClick={async () => {
                try {
                  if (logout) await logout();
                } catch (_) {}
                navigate(`/login${location.search}`, { replace: true });
              }}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium"
            >
              {t('auth.logout')}
            </button>
            <Link
              to="/"
              className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-800 hover:bg-gray-50"
            >
              {t('auth.goToHome')}
            </Link>
          </div>
        </div>
        </div>
      </div>
    );
  }

  // Utilisateur déjà connecté + lien d'invitation investisseur de capitaux (fundInvestorId) : lier le compte puis rediriger.
  if (currentUser && invitationToken && inviteData?.fundInvestorId) {
    if (inviteLoading) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-gray-100">
          <div className="w-full max-w-md p-8 text-center text-gray-600">{t('loading')}</div>
        </div>
      );
    }
    if (linkError) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-gray-100">
          <div className="w-full max-w-md p-8 text-center space-y-4">
            <p className="text-red-600">{linkError}</p>
            <Link to="/" className="inline-block px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
              {t('auth.goToHome')}
            </Link>
          </div>
        </div>
      );
    }
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="w-full max-w-md p-8 text-center text-gray-600">{t('auth.linkingInvestorAccount')}</div>
      </div>
    );
  }

  // Utilisateur déjà connecté + lien d'invitation "client" (avec clientId) : lier le courtier puis rediriger.
  if (currentUser && invitationToken && inviteData?.clientId) {
    if (inviteLoading) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-gray-100">
          <div className="w-full max-w-md p-8 text-center text-gray-600">{t('loading')}</div>
        </div>
      );
    }
    if (linkError) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-gray-100">
          <div className="w-full max-w-md p-8 text-center space-y-4">
            <p className="text-red-600">{linkError}</p>
            <Link to="/" className="inline-block px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
              {t('auth.goToHome')}
            </Link>
          </div>
        </div>
      );
    }
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="w-full max-w-md p-8 text-center text-gray-600">{t('auth.linkingBroker')}</div>
      </div>
    );
  }

  if (inviteLoading && invitationToken) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="w-full max-w-md p-8 text-center text-gray-600">{t('loading')}</div>
      </div>
    );
  }

  if (linkingBroker && inviteData) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100 px-4 py-6">
        <div className="w-full max-w-md p-5 sm:p-8 text-center text-gray-600">{t('auth.linkingBroker')}</div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100 px-4 py-6">
      <div className="w-full max-w-lg space-y-4 sm:space-y-6">
        <div className="flex justify-center">
          <img
            src="/rentalyzer-logo.png"
            alt="Rentalyzer logo"
            className="h-24 sm:h-48 w-auto"
          />
        </div>
        <div className="p-5 sm:p-8 space-y-6 bg-white rounded shadow">
        <h2 className="text-xl sm:text-2xl font-bold text-center">{t('auth.signup.title')}</h2>
        {inviteData && (
          <p className="text-sm text-blue-700 bg-blue-50 p-3 rounded">
            {t('auth.signup.invitedByBroker')}
          </p>
        )}
        {error && (
          <div className="p-2 text-red-600 bg-red-100 rounded space-y-2">
            <p>{error}</p>
            {invitationToken && emailAlreadyInUse && (
              <p className="text-sm">
                <Link to={`/login${location.search}`} className="text-blue-600 underline font-medium">
                  {t('auth.signup.useLoginInstead')}
                </Link>
              </p>
            )}
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          {!isForcedInvestorSignup ? (
            <AccountTypeSignupFields
              selectedPersona={selectedPersona}
              onPersonaChange={setSelectedPersona}
              verification={verification}
              onVerificationChange={setVerification}
            />
          ) : null}
          <div>
            <label className="block text-sm font-medium">{t('auth.email')}</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 border rounded"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium">{t('auth.password')}</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 border rounded"
              required
            />
          </div>
          <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={acceptedLegal}
              onChange={(e) => setAcceptedLegal(e.target.checked)}
              className="mt-1 shrink-0"
              required
            />
            <span>
              {t('signup.legal.acceptPrefix')}{' '}
              <Link to="/terms" className="text-blue-600 hover:underline">
                {t('legal.terms')}
              </Link>{' '}
              {t('signup.legal.acceptAnd')}{' '}
              <Link to="/privacy" className="text-blue-600 hover:underline">
                {t('legal.privacy')}
              </Link>
              {t('signup.legal.acceptSuffix')}
            </span>
          </label>
          <button
            type="submit"
            className="w-full py-2 text-white bg-blue-600 rounded hover:bg-blue-700"
          >
            {t('auth.signup.submit')}
          </button>
        </form>
        <button
          type="button"
          onClick={handleGoogleLogin}
          className="w-full py-2 text-white bg-red-500 rounded hover:bg-red-600"
        >
          {t('auth.google')}
        </button>
        <div className="text-sm text-center">
          {t('auth.haveAccount')}{' '}
          <Link to={`/login${location.search}`} className="text-blue-600 hover:underline">
            {t('auth.login.link')}
          </Link>
        </div>
        <AuthLegalLinks />
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
